import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import { isImage, processImage } from '../../../utils/image';
import { ImageItem } from '../../../utils/form/appointmentForm';
import { VocabularyAttribute } from '../../../types/catalog';
import {
  MAX_PRODUCT_VARIANTS,
  ProductFormValues,
  VariantRowValues,
  emptyVariantRow,
  rowLabel,
  valueLabelsFor,
} from '../../../utils/catalog/productForm';
import { cx } from '../../../helpers/global';
import ValuePicker from './ValuePicker';

interface Props {
  attributes: VocabularyAttribute[];
}

const IMAGE_CELL_SIZE = 40;

// remove stays reachable while the wider columns scroll underneath it
const PINNED_COLUMN_WIDTH = 32;

// runs the full row height so its divider spans the row's padding too
const pinnedActionSx = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  alignSelf: 'stretch',
  my: -0.75,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: theme => theme.palette.background.paper,
  borderLeft: '1px solid',
  borderColor: 'divider',
} satisfies SxProps<Theme>;

interface VariantImageCellProps {
  image: ImageItem | null;
  onChange: (image: ImageItem | null) => void;
}

function VariantImageCell({ image, onChange }: VariantImageCellProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const pick = async (files: FileList | null) => {
    const file = files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    if (!isImage(file)) {
      toast.error('Please select a JPG, PNG, WebP, or HEIC image.');
      return;
    }
    try {
      onChange({
        id: crypto.randomUUID(),
        url: await processImage(file),
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to process image',
      );
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: IMAGE_CELL_SIZE,
        height: IMAGE_CELL_SIZE,
      }}>
      <ButtonBase
        aria-label={image ? 'Replace variant photo' : 'Add variant photo'}
        onClick={() => inputRef.current?.click()}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px dashed',
          borderColor: image ? 'transparent' : 'divider',
          bgcolor: theme => theme.palette.lightGrey.main,
        }}>
        {image ? (
          <Box
            component="img"
            src={image.url}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <AddPhotoAlternateOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary' }}
          />
        )}
      </ButtonBase>
      {image && (
        <IconButton
          aria-label="Remove variant photo"
          onClick={() => onChange(null)}
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            p: 0.25,
            bgcolor: theme => theme.palette.background.paper,
            '&:hover': { bgcolor: theme => theme.palette.background.paper },
          }}>
          <CloseRoundedIcon sx={{ fontSize: 12 }} />
        </IconButton>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={e => pick(e.target.files)}
      />
    </Box>
  );
}

// DollarInput clears to undefined, so anything but a number counts as blank
const moneyValue = (value: number | '' | undefined) =>
  typeof value === 'number' ? String(value) : '';
const parseMoney = (text: string): number | '' =>
  text.trim() === '' ? '' : Math.max(0, Number(text));

// rows mount in pages as the end of the list scrolls into view
const ROWS_PER_PAGE = 200;
// larger tables show text until a cell is focused, so only that cell mounts an input
const ALWAYS_EDITABLE_UP_TO = 30;

const COLUMN_GAP = 8;
const ROW_PADDING_X = 8;

const fixedTrack = (px: number) => ({ css: `${px}px`, min: px });
const flexTrack = (min: number, fr: number) => ({
  css: `minmax(${min}px, ${fr}fr)`,
  min,
});

/** Grid columns, and the row width that fits each one at its minimum. */
function tableColumns(varyingCount: number) {
  const tracks = [
    fixedTrack(IMAGE_CELL_SIZE),
    flexTrack(120, 1.2),
    ...Array.from({ length: varyingCount }, () => flexTrack(120, 1)),
    fixedTrack(96),
    fixedTrack(96),
    fixedTrack(112),
    fixedTrack(PINNED_COLUMN_WIDTH),
  ];
  return {
    columns: tracks.map(track => track.css).join(' '),
    // rows span every track so the sticky remove cell can reach the right edge
    rowMinWidth:
      tracks.reduce((sum, track) => sum + track.min, 0) +
      COLUMN_GAP * (tracks.length - 1) +
      ROW_PADDING_X * 2,
  };
}

const rowSx = (columns: string, rowMinWidth: number): SxProps<Theme> => ({
  display: 'grid',
  gridTemplateColumns: columns,
  gap: `${COLUMN_GAP}px`,
  alignItems: 'center',
  px: `${ROW_PADDING_X}px`,
  minWidth: rowMinWidth,
});

interface TableLayout {
  columns: string;
  rowMinWidth: number;
  varying: VocabularyAttribute[];
}

function VariantHeader({ columns, rowMinWidth, varying }: TableLayout) {
  return (
    <Box
      sx={{
        ...rowSx(columns, rowMinWidth),
        pb: 0.5,
      }}>
      <span />
      <Typography variant="caption" color="text.secondary">
        Label
      </Typography>
      {varying.map(attribute => (
        <Typography
          key={attribute.key}
          variant="caption"
          color="text.secondary">
          {attribute.label}
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        Price
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Cost
      </Typography>
      <Typography variant="caption" color="text.secondary">
        SKU
      </Typography>
      <Box sx={{ ...pinnedActionSx, mt: 0, mb: -0.5 }} />
    </Box>
  );
}

interface EditableCellProps {
  text: string;
  placeholder: string;
  label: string;
  /** Render the input straight away instead of text. */
  alwaysEditing: boolean;
  /** Marks a required cell that is still blank. */
  invalid?: boolean;
  renderInput: (autoFocus: boolean) => React.ReactNode;
}

/** Text until focused; only the cell being edited mounts a real input. */
function EditableCell({
  text,
  placeholder,
  label,
  alwaysEditing,
  invalid = false,
  renderInput,
}: EditableCellProps) {
  const [editing, setEditing] = React.useState(false);
  if (alwaysEditing) {
    return <div className="variant-editing">{renderInput(false)}</div>;
  }
  if (editing) {
    return (
      <div
        className="variant-editing"
        onBlur={e => {
          if (!e.currentTarget.contains(e.relatedTarget)) setEditing(false);
        }}>
        {renderInput(true)}
      </div>
    );
  }
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      className={cx('variant-static', !text && 'empty', invalid && 'invalid')}
      onFocus={() => setEditing(true)}>
      {text || placeholder}
    </span>
  );
}

interface VariantRowProps {
  row: VariantRowValues;
  index: number;
  label: string;
  varying: VocabularyAttribute[];
  optionsByKey: Record<string, string[]>;
  canRemove: boolean;
  alwaysEditing: boolean;
  onUpdate: (index: number, patch: Partial<VariantRowValues>) => void;
  onValueChange: (index: number, key: string, value: string) => void;
  onRemove: (index: number) => void;
}

const dollarAdornment = {
  input: {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  },
};

const VariantRow = React.memo(
  ({
    row,
    index,
    label,
    varying,
    optionsByKey,
    canRemove,
    alwaysEditing,
    onUpdate,
    onValueChange,
    onRemove,
  }: VariantRowProps) => (
    <div className="variant-row">
      <VariantImageCell
        image={row.image}
        onChange={image => onUpdate(index, { image })}
      />
      <span className="variant-label">{label}</span>
      {varying.map(attribute => (
        <EditableCell
          alwaysEditing={alwaysEditing}
          key={attribute.key}
          text={row.values[attribute.key] ?? ''}
          placeholder={attribute.label}
          label={attribute.label}
          renderInput={autoFocus => (
            <ValuePicker
              autoFocus={autoFocus}
              size="small"
              value={row.values[attribute.key] ?? ''}
              options={optionsByKey[attribute.key] ?? []}
              placeholder={attribute.label}
              onChange={value => onValueChange(index, attribute.key, value)}
            />
          )}
        />
      ))}
      <EditableCell
        alwaysEditing={alwaysEditing}
        text={moneyValue(row.price) && `$ ${row.price}`}
        placeholder="Price required"
        label="price"
        invalid={!moneyValue(row.price)}
        renderInput={autoFocus => (
          <TextField
            autoFocus={autoFocus}
            size="small"
            type="number"
            error={!moneyValue(row.price)}
            value={moneyValue(row.price)}
            placeholder="0.00"
            onChange={e =>
              onUpdate(index, { price: parseMoney(e.target.value) })
            }
            slotProps={dollarAdornment}
          />
        )}
      />
      <EditableCell
        alwaysEditing={alwaysEditing}
        text={moneyValue(row.cost) && `$ ${row.cost}`}
        placeholder="$ 0.00"
        label="cost"
        renderInput={autoFocus => (
          <TextField
            autoFocus={autoFocus}
            size="small"
            type="number"
            value={moneyValue(row.cost)}
            placeholder="0.00"
            onChange={e =>
              onUpdate(index, { cost: parseMoney(e.target.value) })
            }
            slotProps={dollarAdornment}
          />
        )}
      />
      <EditableCell
        alwaysEditing={alwaysEditing}
        text={row.sku}
        placeholder="SKU"
        label="SKU"
        renderInput={autoFocus => (
          <TextField
            autoFocus={autoFocus}
            size="small"
            value={row.sku}
            placeholder="SKU"
            onChange={e => onUpdate(index, { sku: e.target.value })}
          />
        )}
      />
      <Box sx={pinnedActionSx}>
        <IconButton
          size="small"
          sx={{ p: 0.5 }}
          aria-label="Remove variant"
          disabled={!canRemove}
          onClick={() => onRemove(index)}>
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </div>
  ),
);

const rowKey = (index: number, row: VariantRowValues) =>
  row.id ?? row.clientKey ?? `new-${index}`;

export default function VariantRows({ attributes }: Props) {
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();
  const { varyingKeys, variants, category } = values;
  const [shown, setShown] = React.useState(ROWS_PER_PAGE);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  // handlers read the latest values so memoized rows keep stable callbacks
  const latest = React.useRef(values);
  latest.current = values;
  const canObserve = typeof IntersectionObserver !== 'undefined';

  const varying = React.useMemo(
    () =>
      varyingKeys
        .map(key => attributes.find(a => a.key === key))
        .filter((a): a is VocabularyAttribute => !!a),
    [varyingKeys, attributes],
  );
  const optionsByKey = React.useMemo(
    () =>
      Object.fromEntries(
        varying.map(a => [a.key, valueLabelsFor(a, category)]),
      ),
    [varying, category],
  );

  const update = React.useCallback(
    (index: number, patch: Partial<VariantRowValues>) =>
      setFieldValue(
        'variants',
        latest.current.variants.map((row, i) =>
          i === index ? { ...row, ...patch } : row,
        ),
      ),
    [setFieldValue],
  );

  const changeValue = React.useCallback(
    (index: number, key: string, value: string) => {
      const { current } = latest;
      update(index, {
        values: { ...current.variants[index].values, [key]: value },
      });
      const axis = current.axisValues[key] ?? [];
      if (value && !axis.includes(value)) {
        setFieldValue(`axisValues.${key}`, [...axis, value]);
      }
    },
    [update, setFieldValue],
  );

  const removeRow = React.useCallback(
    (index: number) =>
      setFieldValue(
        'variants',
        latest.current.variants.filter((_, i) => i !== index),
      ),
    [setFieldValue],
  );

  const addRow = () => {
    const last = variants[variants.length - 1];
    const copy: VariantRowValues = last
      ? {
          ...last,
          id: undefined,
          clientKey: crypto.randomUUID(),
          values: { ...last.values },
          sku: '',
          image: null,
        }
      : emptyVariantRow();
    setFieldValue('variants', [...variants, copy]);
  };

  const hasMore = shown < variants.length;
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!hasMore || !sentinel || !canObserve) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShown(n => n + ROWS_PER_PAGE);
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, shown, canObserve]);

  const { columns, rowMinWidth } = tableColumns(varying.length);

  return (
    <Box className="input-group" sx={{ '& > *': { width: '100%' } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}>
        <Typography variant="h5">Variants ({variants.length})</Typography>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          disabled={variants.length >= MAX_PRODUCT_VARIANTS}
          onClick={addRow}>
          Add variant
        </Button>
      </Box>
      <Box
        sx={theme => ({
          overflowX: 'auto',
          // MUI's small fields are 40px with 16px text; match the form's 14px rows
          '& .MuiInputBase-root': {
            height: 34,
            fontSize: theme.typography.body2.fontSize,
          },
          '& .MuiInputBase-input, & .MuiAutocomplete-inputRoot .MuiAutocomplete-input':
            { py: 0 },
          '& .MuiAutocomplete-inputRoot': { py: 0 },
          // rows style through classes here so thousands of them skip per-row sx
          '& .variant-row': {
            display: 'grid',
            gridTemplateColumns: columns,
            gap: `${COLUMN_GAP}px`,
            alignItems: 'center',
            padding: `${theme.spacing(0.75)} ${ROW_PADDING_X}px`,
            borderTop: `1px solid ${theme.palette.divider}`,
            minWidth: rowMinWidth,
          },
          '& .variant-label, & .variant-static': {
            ...theme.typography.body2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            minWidth: 0,
          },
          '& .variant-static': {
            height: 34,
            lineHeight: '32px',
            px: '14px',
            boxSizing: 'border-box',
            border: `1px solid ${theme.palette.divider}`,
            // filter.css rounds every outlined input to 10px globally
            borderRadius: '10px',
            cursor: 'text',
          },
          '& .variant-static:hover': {
            borderColor: theme.palette.text.primary,
          },
          '& .variant-static.empty': { color: theme.palette.text.disabled },
          '& .variant-static.invalid': {
            borderColor: theme.palette.error.main,
            color: theme.palette.error.main,
          },
          '& .variant-editing': { minWidth: 0 },
        })}>
        <VariantHeader
          columns={columns}
          rowMinWidth={rowMinWidth}
          varying={varying}
        />
        {variants.slice(0, shown).map((row, index) => (
          <VariantRow
            key={rowKey(index, row)}
            varying={varying}
            row={row}
            index={index}
            label={
              rowLabel(row, varyingKeys, attributes) ||
              row.overrideLabel ||
              values.name ||
              'Variant'
            }
            optionsByKey={optionsByKey}
            canRemove={variants.length > 1}
            alwaysEditing={variants.length <= ALWAYS_EDITABLE_UP_TO}
            onUpdate={update}
            onValueChange={changeValue}
            onRemove={removeRow}
          />
        ))}
      </Box>
      {hasMore && <Box ref={sentinelRef} height={1} />}
    </Box>
  );
}
